import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

import ContactModal from "../../../../Assets/Common/ContactModal";

const CTASection = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const handleGetStarted = () => {
    setIsContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
  };

  return (
    <>
      <section
        className="
          relative
          overflow-hidden
          theme-bg-primary
          py-2
          sm:py-4
          md:py-6
          lg:py-10
        "
      >
        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            h-72
            w-72
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-orange-500/[0.06]
            blur-[120px]
            sm:h-96
            sm:w-96
          "
        />

        <div
          className="
            relative
            mx-auto
            w-full
            max-w-7xl
            px-4
            sm:px-6
            lg:px-8
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.6,
              ease: "easeOut",
            }}
            className="
              relative
              overflow-hidden
              rounded-2xl
              border
              theme-border
              theme-card
              px-5
              py-8
              sm:rounded-3xl
              sm:px-8
              sm:py-10
              md:px-12
              md:py-12
              lg:px-14
              lg:py-14
            "
          >
            <div
              className="
                pointer-events-none
                absolute
                -right-20
                -top-20
                h-48
                w-48
                rounded-full
                bg-orange-500/[0.06]
                blur-[80px]
                sm:h-64
                sm:w-64
              "
            />

            <div
              className="
                relative
                flex
                flex-col
                items-center
                justify-between
                gap-8
                text-center
                lg:flex-row
                lg:gap-12
                lg:text-left
              "
            >
              <div className="max-w-2xl">
                <span
                  className="
                    inline-flex
                    rounded-full
                    border
                    border-orange-500/20
                    bg-orange-500/[0.08]
                    px-3
                    py-1.5
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[2px]
                    brand-color
                    sm:px-4
                    sm:py-2
                    sm:text-xs
                  "
                >
                  Let's Get Started
                </span>

                <h2
                  className="
                    mt-4
                    text-2xl
                    font-bold
                    leading-tight
                    tracking-tight
                    theme-text-primary
                    sm:mt-5
                    sm:text-3xl
                    md:text-4xl
                    lg:text-5xl
                  "
                >
                  Ready to Power Your{" "}
                  <span className="brand-color">
                    Business With APIs?
                  </span>
                </h2>

                <p
                  className="
                    mt-3
                    max-w-xl
                    text-xs
                    leading-6
                    theme-text-muted
                    sm:mt-4
                    sm:text-sm
                    sm:leading-7
                    md:text-base
                  "
                >
                  Connect your applications with reliable WhatsApp messaging,
                  SMS OTP authentication, and powerful API solutions designed
                  to simplify communication and business workflows.
                </p>

                <div
                  className="
                    mt-6
                    flex
                    flex-col
                    gap-3
                    sm:mt-7
                    sm:flex-row
                    sm:justify-center
                    lg:justify-start
                  "
                >
                  <button
                    type="button"
                    onClick={handleGetStarted}
                    className="
                      group
                      inline-flex
                      items-center
                      justify-center
                      gap-2
                      rounded-xl
                      brand-bg
                      px-5
                      py-3
                      text-sm
                      font-semibold
                      text-white
                      transition-all
                      duration-300
                      hover:-translate-y-0.5
                      sm:px-6
                      sm:py-3.5
                    "
                  >
                    <span>Get Started</span>

                    <span
                      className="
                        flex
                        h-5
                        w-5
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-white/10
                        transition-transform
                        duration-300
                        group-hover:translate-x-1
                        sm:h-6
                        sm:w-6
                      "
                    >
                      <ArrowRight
                        size={14}
                        strokeWidth={2.2}
                        className="sm:h-[15px] sm:w-[15px]"
                      />
                    </span>
                  </button>
                </div>
              </div>

              <div
                className="
                  grid
                  w-full
                  max-w-sm
                  grid-cols-2
                  gap-3
                  sm:gap-4
                  lg:max-w-xs
                "
              >
                {[
                  {
                    number: "24/7",
                    label: "API Availability",
                  },
                  {
                    number: "99.9%",
                    label: "Reliable Delivery",
                  },
                  {
                    number: "2",
                    label: "API Solutions",
                  },
                  {
                    number: "24/7",
                    label: "Technical Support",
                  },
                ].map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{
                      opacity: 0,
                      y: 15,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      duration: 0.4,
                      delay: 0.15 + index * 0.06,
                    }}
                    whileHover={{
                      y: -3,
                    }}
                    className="
                      rounded-xl
                      border
                      theme-border
                      theme-card-secondary
                      px-3
                      py-4
                      text-center
                      transition-colors
                      duration-300
                      hover:border-orange-500/30
                      sm:rounded-2xl
                      sm:px-4
                      sm:py-5
                    "
                  >
                    <h3
                      className="
                        text-xl
                        font-bold
                        brand-color
                        sm:text-2xl
                      "
                    >
                      {item.number}
                    </h3>

                    <p
                      className="
                        mt-1
                        text-[10px]
                        theme-text-muted
                        sm:text-xs
                      "
                    >
                      {item.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {isContactModalOpen && (
          <ContactModal
            isOpen={isContactModalOpen}
            onClose={handleCloseContactModal}
            initialService="API Services"
            initialSubService=""
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CTASection;